import { Close } from '@mui/icons-material';
import type {
  Breakpoint,
  DialogActionsProps,
  DialogContentProps,
  DialogProps,
  DialogSlotsAndSlotProps,
  DialogTitleProps,
} from '@mui/material';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  useMediaQuery,
} from '@mui/material';
import type { ReactNode } from 'react';

import type { Or2DialogProgressBarProps } from './Or2DialogProgressBar';
import { Or2DialogProgressBar } from './Or2DialogProgressBar';

export interface Or2DialogProps extends Omit<
  DialogProps,
  'slotProps' | 'title'
> {
  open: boolean;
  title?: ReactNode;
  header?: ReactNode;
  ariaLabel?: string;
  disableBackdropClick?: boolean;
  actions?: ReactNode;
  loading?: boolean;
  small?: boolean;
  hideCloseButton?: boolean;
  disableCloseButton?: boolean;
  onClose: () => void;
  removeHeaderPadding?: boolean;
  fullScreenThreshold?: Breakpoint;
  hasLoadingBar?: boolean;
  slotProps?: DialogSlotsAndSlotProps['slotProps'] & {
    title?: DialogTitleProps;
    content?: DialogContentProps;
    actions?: DialogActionsProps;
    or2DialogProgressBar?: Or2DialogProgressBarProps;
  };
}

// You must add your own aria-label to the dialog description
// ${ariaLabel}-dialog-description
export const Or2Dialog = ({
  open,
  title,
  header,
  ariaLabel,
  disableBackdropClick,
  disableEscapeKeyDown,
  children,
  actions,
  loading,
  onClose,
  small = false,
  hideCloseButton,
  disableCloseButton,
  removeHeaderPadding,
  fullScreen,
  fullScreenThreshold,
  slotProps,
  hasLoadingBar = true,
  ...other
}: Or2DialogProps) => {
  const isBelowFullScreenThreshold = useMediaQuery((theme) =>
    theme.breakpoints.down(fullScreenThreshold ?? 0),
  );

  const isFullScreen = fullScreen === true || isBelowFullScreenThreshold;

  return (
    <Dialog
      aria-labelledby={`${ariaLabel}-dialog-title`}
      aria-describedby={`${ariaLabel}-dialog-description`}
      maxWidth={small ? 'sm' : 'md'}
      slotProps={slotProps}
      open={open}
      onClose={(_, reason) => {
        if (disableBackdropClick && reason === 'backdropClick') return;
        if (disableEscapeKeyDown && reason === 'escapeKeyDown') return;
        onClose();
      }}
      fullWidth={!small}
      fullScreen={isFullScreen}
      {...other}
    >
      {!hideCloseButton && (
        <IconButton
          aria-label="close"
          onClick={onClose}
          disabled={disableCloseButton}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            zIndex: 1,
            color: theme.palette.grey[500],
          })}
        >
          <Close />
        </IconButton>
      )}
      {header && header}
      {title && (
        <DialogTitle
          aria-label={`${ariaLabel}-dialog-title`}
          sx={[removeHeaderPadding ? { paddingBottom: 1 } : {}]}
          {...slotProps?.title}
        >
          {title}
        </DialogTitle>
      )}
      <DialogContent {...slotProps?.content}>
        <Box sx={[removeHeaderPadding ? { paddingTop: 1 } : {}]}>
          {children}
        </Box>
      </DialogContent>
      {actions && (
        <DialogActions {...slotProps?.actions}>{actions}</DialogActions>
      )}
      {hasLoadingBar && <Or2DialogProgressBar loading={loading} />}
    </Dialog>
  );
};
